import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner, InputBox } from "../../Common";
import styles from "./planConfig.module.css";
import { getPlansDetails, CreateplanConfig } from "../../api";
import { validatePlanConfigArrayField } from "../../utils/validation";

const PlanConfig = ({ planCode, onBack, refreshPlanData }) => {
  const [planData, setPlanData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState({});
  const [success, setSuccess] = useState({});
  const [openSections, setOpenSections] = useState({});

  const sections = [
    "planDetails",
    "features",
    "featuresv2",
    "premiumFeatures",
    "tickerCommission",
    "conditionalText",
  ];

  useEffect(() => {
    fetchPlanData();
  }, [planCode]);

  const fetchPlanData = async () => {
    setLoading(true);
    setError({});

    try {
      const response = await getPlansDetails(planCode);
      if (response.status === 200 && response.data?.code === 200) {
        const plan = response.data.data?.[0];

        if (!plan) {
          setError({ general: "No plan data found." });
          return;
        }

        const baseFields = {
          planTitle: "",
          planSubtitle1: "",
          planSubtitle2: "",
          ...plan,
        };

        const configMap = {
          features: [],
          featuresv2: [],
          premiumFeatures: [],
          tickerCommission: [],
          conditionalText: "",
          planTitle: "",
          planSubtitle1: "",
          planSubtitle2: "",
        };

        if (Array.isArray(plan?.planConfig)) {
          plan.planConfig.forEach(({ key, value }) => {
            switch (key) {
              case "features":
                configMap.features.push(value);
                break;

              case "featuresv2":
                configMap.featuresv2.push(value);
                break;

              case "premiumFeatures":
                configMap.premiumFeatures.push(value?.feature || value);
                break;

              case "tickerCommission":
                configMap.tickerCommission.push(value);
                break;

              case "conditionalText":
                configMap.conditionalText = value?.text || value || "";
                break;

              case "planTitle":
                configMap.planTitle = value;
                break;
              case "planSubtitle1":
                configMap.planSubtitle1 = value;
                break;
              case "planSubtitle2":
                configMap.planSubtitle2 = value;
                break;
              default:
                break;
            }
          });
        }
        if (configMap.features.length === 0) {
          configMap.features.push({ text: "", toolTips: "", type: "" });
        }
        if (configMap.featuresv2.length === 0) {
          configMap.featuresv2.push({ featureName: "", description: "" });
        }
        if (configMap.premiumFeatures.length === 0) {
          configMap.premiumFeatures.push("");
        }
        if (configMap.tickerCommission.length === 0) {
          configMap.tickerCommission.push({
            ticker: "",
            tradeCommission: 0,
            shareCommission: 0,
            aumCommission: 0,
            minCommission: 0,
          });
        }

        const finalData = {
          ...baseFields,
          ...configMap,
        };

        setPlanData(finalData);
      } else {
        setError({ general: "Invalid response from server." });
      }
    } catch (err) {
      setError({ general: "Error fetching plan data." });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => {
      const newState = {};
      sections.forEach((s) => {
        newState[s] = false;
      });
      newState[section] = !prev[section];
      return newState;
    });
    setError((prev) => ({ ...prev, [section]: "" }));
    setSuccess((prev) => ({ ...prev, [section]: "" }));
  };

  const handleInputChange = (section, field, value, index = null) => {
    setPlanData((prev) => {
      const newData = { ...prev };

      if (index !== null) {
        if (!newData[section]) newData[section] = [];
        if (!newData[section][index]) newData[section][index] = {};
        newData[section][index][field] = value;
      } else if (Array.isArray(newData[section])) {
        newData[section] = value;
      } else if (section === "planDetails") {
        newData[field] = value;
      } else {
        newData[section] = value;
      }

      return newData;
    });
  };

  const addArrayItem = (section) => {
    setPlanData((prev) => {
      const newData = { ...prev };
      if (!newData[section]) newData[section] = [];

      switch (section) {
        case "features":
          newData[section].push({ text: "", toolTips: "", type: "" });
          break;
        case "featuresv2":
          newData[section].push({ featureName: "", description: "" });
          break;
        case "tickerCommission":
          newData[section].push({
            ticker: "",
            tradeCommission: 0,
            shareCommission: 0,
            aumCommission: 0,
            minCommission: 0,
          });
          break;
        case "premiumFeatures":
          newData[section].push("");
          break;
        default:
          break;
      }

      return newData;
    });
  };

  const removeArrayItem = (section, index) => {
    setPlanData((prev) => {
      const newData = { ...prev };
      if (newData[section] && Array.isArray(newData[section])) {
        newData[section].splice(index, 1);
      }
      return newData;
    });
  };

  const handleSaveSection = async (section) => {
    setSaving((prev) => ({ ...prev, [section]: true }));
    setError((prev) => ({ ...prev, [section]: "" }));
    setSuccess((prev) => ({ ...prev, [section]: "" }));

    if (section === "features") {
      const isEmpty = validatePlanConfigArrayField(planData[section], "text");
      if (isEmpty) {
        setError((prev) => ({
          ...prev,
          [section]: "Please enter text for all features.",
        }));
        setSaving((prev) => ({ ...prev, [section]: false }));
        return;
      }
    }

    if (section === "featuresv2") {
      const isEmpty = validatePlanConfigArrayField(
        planData[section],
        "featureName"
      );
      if (isEmpty) {
        setError((prev) => ({
          ...prev,
          [section]: "Please enter feature name for all features.",
        }));
        setSaving((prev) => ({ ...prev, [section]: false }));
        return;
      }
    }

    if (section === "premiumFeatures") {
      const isEmpty = planData[section].some((s) => {
        if (typeof s === "string") {
          return s.trim().length === 0;
        }
        if (typeof s === "object" && s !== null && "feature" in s) {
          return !s.feature || s.feature.trim().length === 0;
        }
        return true;
      });
      if (isEmpty) {
        setError((prev) => ({
          ...prev,
          [section]: "Please enter premium feature",
        }));
        setSaving((prev) => ({ ...prev, [section]: false }));
        return;
      }
    }

    if (section === "tickerCommission") {
      const isEmpty = planData[section].some(
        (item) => !item.ticker || item.ticker.trim().length === 0
      );
      if (isEmpty) {
        setError((prev) => ({
          ...prev,
          [section]: "Please enter ticker items for all.",
        }));
        setSaving((prev) => ({ ...prev, [section]: false }));
        return;
      }
    }

    try {
      let payload = {
        code: planData.code || planCode,
      };

      if (section === "planDetails") {
        payload = {
          ...payload,
          planTitle: planData.planTitle || "",
          planSubtitle1: planData.planSubtitle1 || "",
          planSubtitle2: planData.planSubtitle2 || "",
        };
      } else {
        if (section === "conditionalText") {
          payload[section] = [planData[section] || ""];
        } else {
          payload[section] = planData[section] || [];
        }
      }
      const response = await CreateplanConfig(payload);
      if (response.status === 200 || response.code === 200) {
        setSuccess((prev) => ({
          ...prev,
          [section]: `${getSectionTitle(section)} saved successfully`,
        }));
        setTimeout(
          () => setSuccess((prev) => ({ ...prev, [section]: "" })),
          3000
        );
        if (refreshPlanData) {
          refreshPlanData();
        }
      } else {
        throw new Error(`Save failed: ${response.message || "Unknown error"}`);
      }
    } catch (err) {
      setError((prev) => ({
        ...prev,
        [section]: `Error saving configuration: ${err.message}`,
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [section]: false }));
    }
  };

  const RichTextEditor = React.memo(({ value, onChange, placeholder }) => {
    const editorRef = useRef(null);
    const [localValue, setLocalValue] = useState(value || "");
    const hasInitializedRef = useRef(false);

    useEffect(() => {
      setLocalValue(value || "");
      if (editorRef.current && !hasInitializedRef.current) {
        editorRef.current.innerHTML = value || "";
        hasInitializedRef.current = true;
      }
    }, [value]);

    const emitChange = () => {
      if (editorRef.current) {
        const html = editorRef.current.innerHTML;
        setLocalValue(html);
      }
    };

    const handleBlur = () => {
      if (localValue !== value && onChange) {
        onChange(localValue);
      }
    };

    const execCommand = (command) => {
      document.execCommand(command, false, null);
      emitChange();
    };

    return (
      <div className={styles.richeditorwrapper}>
        <fieldset className={styles.richeditorcontent}>
          <div
            contentEditable
            ref={editorRef}
            onInput={emitChange}
            onBlur={handleBlur}
            data-placeholder={placeholder}
            suppressContentEditableWarning
          />
        </fieldset>
        <fieldset className={styles.richeditortoolbar}>
          <button
            className={styles.fontStyle}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand("italic")}
            title="Italic Highlighted Text"
          >
            <i>I</i>
          </button>
          <button
            className={styles.fontStyle}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand("bold")}
            title="Bold Highlighted Text"
          >
            <b>B</b>
          </button>
          <button
            className={styles.fontStyle}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand("underline")}
            title="Underline Highlighted Text"
          >
            <u>U</u>
          </button>
        </fieldset>
      </div>
    );
  });

  const renderPlanDetails = () => (
    <div className={styles.planDetailsGrid}>
      <div className={styles.planDetailsGroup}>
        <label className={styles.planDetailsLabel}>Plan Title</label>
        <InputBox
          className={styles.planDetailsInput}
          value={planData.planTitle || ""}
          onChange={(e) =>
            handleInputChange("planDetails", "planTitle", e.target.value)
          }
          placeholder="Enter plan title"
        />
      </div>
      <div className={styles.planDetailsGroup}>
        <label className={styles.planDetailsLabel}>Plan Subtitle 1</label>
        <InputBox
          className={styles.planDetailsInput}
          value={planData.planSubtitle1 || ""}
          onChange={(e) =>
            handleInputChange("planDetails", "planSubtitle1", e.target.value)
          }
          placeholder="Enter plan subtitle 1"
        />
      </div>
      <div className={styles.planDetailsGroup}>
        <label className={styles.planDetailsLabel}>Plan Subtitle 2</label>
        <InputBox
          className={styles.planDetailsInput}
          value={planData.planSubtitle2 || ""}
          onChange={(e) =>
            handleInputChange("planDetails", "planSubtitle2", e.target.value)
          }
          placeholder="Enter plan subtitle 2"
        />
      </div>
      <div className={styles.planDetailsButtonRow}>
        <div className={styles.planDetailsActions}>
          <Button
            title="Save Plan Details"
            onClick={() => handleSaveSection("planDetails")}
            disabled={
              saving["planDetails"] ||
              !(
                (planData?.planTitle && planData.planTitle.trim().length > 0) ||
                (planData?.planSubtitle1 &&
                  planData.planSubtitle1.trim().length > 0) ||
                (planData?.planSubtitle2 &&
                  planData.planSubtitle2.trim().length > 0)
              )
            }
            loading={saving["planDetails"]}
          />
        </div>
      </div>
    </div>
  );

  const renderFeatures = () => (
    <div className={styles.featuresSectionWrapper}>
      <div className={styles.featuresSectionGrid}>
        {planData?.features?.map((feature, index) => (
          <div key={index} className={styles.featuresSectionCard}>
            <div className={styles.featuresSectionCardHeader}>
              <h5 className={styles.featuresSectionTitle}>
                Feature {index + 1}
              </h5>
              <button
                className={`${styles.featuresSectionDeleteBtn} ${
                  planData?.features?.length === 1
                    ? styles.featuresSectionDeleteBtnDisabled
                    : ""
                }`}
                onClick={() => removeArrayItem("features", index)}
                title="Remove Feature"
                disabled={planData?.features?.length === 1}
              >
                X
              </button>
            </div>
            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                Feature Name
                <span className={styles.featuresSectionRequired}>*</span>
              </label>
              <RichTextEditor
                key={`feature-${index}`}
                value={feature.text || ""}
                onChange={(value) =>
                  handleInputChange("features", "text", value, index)
                }
                placeholder="Enter feature text"
              />
            </div>
            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>Tooltips</label>
              <InputBox
                value={feature.toolTips || ""}
                onChange={(e) =>
                  handleInputChange(
                    "features",
                    "toolTips",
                    e.target.value,
                    index
                  )
                }
                placeholder="Enter tooltip text"
              />
            </div>
            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>Type</label>
              <InputBox
                value={feature.type || ""}
                onChange={(e) =>
                  handleInputChange("features", "type", e.target.value, index)
                }
                placeholder="Enter feature type"
              />
            </div>
          </div>
        ))}
      </div>
      {error["features"] && (
        <div className={styles.featuresSectionRequired}>
          {error["features"]}
        </div>
      )}
      <div className={styles.featuresSectionButtonRow}>
        <div className={styles.featuresSectionActions}>
          <Button
            title="Add Feature"
            onClick={() => addArrayItem("features")}
          />
        </div>
        <div className={styles.featuresSectionActions}>
          <Button
            title={`Save ${getSectionTitle("features")}`}
            onClick={() => handleSaveSection("features")}
            loading={saving["features"]}
            disabled={!planData?.features?.length || saving["features"]}
          />
        </div>
      </div>
    </div>
  );

  const renderFeaturesV2 = () => (
    <div className={styles.featuresSectionWrapper}>
      <div className={styles.featuresSectionGrid}>
        {planData.featuresv2?.map((feature, index) => (
          <div key={index} className={styles.featuresSectionCard}>
            <div className={styles.featuresSectionCardHeader}>
              <h5 className={styles.featuresSectionTitle}>
                Feature V2 {index + 1}
              </h5>
              <button
                className={`${styles.featuresSectionDeleteBtn} ${
                  planData?.featuresv2?.length === 1
                    ? styles.featuresSectionDeleteBtnDisabled
                    : ""
                }`}
                onClick={() => removeArrayItem("featuresv2", index)}
                title="Remove Feature V2"
                disabled={planData?.featuresv2?.length === 1}
              >
                X
              </button>
            </div>
            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                Feature Name{" "}
                <span className={styles.featuresSectionRequired}>*</span>
              </label>
              <RichTextEditor
                key={`featurev2-${index}`}
                value={feature.featureName || ""}
                onChange={(value) =>
                  handleInputChange("featuresv2", "featureName", value, index)
                }
                placeholder="Enter feature name"
              />
            </div>
            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>Description</label>
              <textarea
                className={styles.textArea}
                value={feature.description || ""}
                onChange={(e) =>
                  handleInputChange(
                    "featuresv2",
                    "description",
                    e.target.value,
                    index
                  )
                }
                placeholder="Enter feature description"
                rows={3}
              />
            </div>
          </div>
        ))}
      </div>
      {error["featuresv2"] && (
        <div className={styles.featuresSectionRequired}>
          {error["featuresv2"]}
        </div>
      )}
      <div className={styles.featuresSectionButtonRow}>
        <div className={styles.featuresSectionActions}>
          <Button
            title="Add Feature V2"
            onClick={() => addArrayItem("featuresv2")}
          />
        </div>
        <div className={styles.featuresSectionActions}>
          <Button
            title={`Save ${getSectionTitle("featuresv2")}`}
            onClick={() => handleSaveSection("featuresv2")}
            disabled={!planData.featuresv2?.length || saving["featuresv2"]}
            loading={saving["featuresv2"]}
          />
        </div>
      </div>
    </div>
  );

  const renderPremiumFeatures = () => (
    <div className={styles.premiumFeatureWrapper}>
      <div className={styles.premiumFeatureList}>
        {planData.premiumFeatures?.map((feature, index) => (
          <div key={index} className={styles.premiumFeatureRow}>
            <InputBox
              className={styles.premiumFeatureInput}
              value={feature || ""}
              onChange={(e) => {
                const newFeatures = [...(planData.premiumFeatures || [])];
                newFeatures[index] = e.target.value;
                handleInputChange("premiumFeatures", null, newFeatures);
              }}
              placeholder="Enter premium feature"
            />
            <button
              className={`${styles.featuresSectionDeleteBtn} ${
                planData?.premiumFeatures?.length === 1
                  ? styles.featuresSectionDeleteBtnDisabled
                  : ""
              }`}
              onClick={() => removeArrayItem("premiumFeatures", index)}
              title="Remove"
              disabled={planData?.premiumFeatures?.length === 1}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {error["premiumFeatures"] && (
        <div className={styles.featuresSectionRequired}>
          {error["premiumFeatures"]}
        </div>
      )}

      <div className={styles.premiumFeatureButtonRow}>
        <div className={styles.premiumFeatureActions}>
          <Button
            title="Add Premium Feature"
            onClick={() => addArrayItem("premiumFeatures")}
          />
        </div>
        <div className={styles.premiumFeatureActions}>
          <Button
            title={`Save ${getSectionTitle("premiumFeatures")}`}
            onClick={() => handleSaveSection("premiumFeatures")}
            disabled={
              !planData.premiumFeatures?.length || saving["premiumFeatures"]
            }
            loading={saving["premiumFeatures"]}
          />
        </div>
      </div>
    </div>
  );
  const renderTickerCommission = () => (
    <div className={styles.featuresSectionWrapper}>
      <div className={styles.featuresSectionGrid}>
        {planData.tickerCommission?.map((commission, index) => (
          <div key={index} className={styles.featuresSectionCard}>
            <div className={styles.featuresSectionCardHeader}>
              <h5 className={styles.featuresSectionTitle}>
                Ticker Commission {index + 1}
              </h5>
              <button
                onClick={() => removeArrayItem("tickerCommission", index)}
                title="Remove Ticker Commission"
                className={`${styles.featuresSectionDeleteBtn} ${
                  planData?.tickerCommission?.length === 1
                    ? styles.featuresSectionDeleteBtnDisabled
                    : ""
                }`}
              >
                ✕
              </button>
            </div>

            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>Ticker<span className={styles.featuresSectionRequired}>*</span></label>
              <InputBox
                className={styles.featuresSectionInput}
                value={commission.ticker || ""}
                onChange={(e) =>
                  handleInputChange(
                    "tickerCommission",
                    "ticker",
                    e.target.value,
                    index
                  )
                }
                placeholder="Enter ticker symbol"
              />
            </div>

            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                Trade Commission
              </label>
              <InputBox
                className={styles.featuresSectionInput}
                type="number"
                value={commission.tradeCommission || 0}
                onChange={(e) =>
                  handleInputChange(
                    "tickerCommission",
                    "tradeCommission",
                    parseFloat(e.target.value) || 0,
                    index
                  )
                }
                placeholder="0.00"
              />
            </div>

            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                Share Commission
              </label>
              <InputBox
                className={styles.featuresSectionInput}
                type="number"
                value={commission.shareCommission || 0}
                onChange={(e) =>
                  handleInputChange(
                    "tickerCommission",
                    "shareCommission",
                    parseFloat(e.target.value) || 0,
                    index
                  )
                }
                placeholder="0.00"
              />
            </div>

            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                AUM Commission
              </label>
              <InputBox
                className={styles.featuresSectionInput}
                type="number"
                step="0.1"
                value={commission.aumCommission || 0}
                onChange={(e) =>
                  handleInputChange(
                    "tickerCommission",
                    "aumCommission",
                    parseFloat(e.target.value) || 0,
                    index
                  )
                }
                placeholder="0.0"
              />
            </div>

            <div className={styles.featuresSectionFormGroup}>
              <label className={styles.featuresSectionLabel}>
                Min Commission
              </label>
              <InputBox
                className={styles.featuresSectionInput}
                type="number"
                value={commission.minCommission || 0}
                onChange={(e) =>
                  handleInputChange(
                    "tickerCommission",
                    "minCommission",
                    parseFloat(e.target.value) || 0,
                    index
                  )
                }
                placeholder="0.00"
              />
            </div>
          </div>
        ))}
      </div>
      {error["tickerCommission"] && (
        <div className={styles.featuresSectionRequired}>
          {error["tickerCommission"]}
        </div>
      )}

      <div className={styles.featuresSectionButtonRow}>
        <div className={styles.featuresSectionActions}>
          <Button
            title="Add Ticker Commission"
            onClick={() => addArrayItem("tickerCommission")}
          />
        </div>
        <div className={styles.featuresSectionActions}>
          <Button
            title={`Save ${getSectionTitle("tickerCommission")}`}
            onClick={() => handleSaveSection("tickerCommission")}
            disabled={
              !planData.tickerCommission?.length || saving["tickerCommission"]
            }
            loading={saving["tickerCommission"]}
          />
        </div>
      </div>
    </div>
  );
  const renderConditionalText = () => (
    <div className={styles.conditionalTextWrapper}>
      <div className={styles.formGroup}>
        <textarea
          className={styles.textArea}
          value={planData.conditionalText || ""}
          onChange={(e) =>
            handleInputChange(
              "conditionalText",
              "conditionalText",
              e.target.value
            )
          }
          placeholder="Enter conditional text"
          rows={4}
        />
      </div>
      <div className={styles.buttonRow}>
        <div className={styles.actions}>
          <Button
            title="Save Conditional Text"
            onClick={() => handleSaveSection("conditionalText")}
            disabled={
              !(
                planData?.conditionalText &&
                planData.conditionalText.trim().length > 0
              ) || saving["conditionalText"]
            }
            loading={saving["conditionalText"]}
          />
        </div>
      </div>
    </div>
  );

  const getSectionTitle = (section) => {
    const titles = {
      planDetails: "Plan Details",
      features: "Features",
      featuresv2: "Features V2",
      premiumFeatures: "Premium Features",
      tickerCommission: "Ticker Commission",
      conditionalText: "Conditional Text",
    };
    return titles[section] || section;
  };

  const renderSectionContent = (section) => {
    switch (section) {
      case "planDetails":
        return renderPlanDetails();
      case "features":
        return renderFeatures();
      case "featuresv2":
        return renderFeaturesV2();
      case "premiumFeatures":
        return renderPremiumFeatures();
      case "tickerCommission":
        return renderTickerCommission();
      case "conditionalText":
        return renderConditionalText();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.configContainer}>
      <div className={styles.header}>
        <h4>Plan Configuration: {planCode}</h4>
      </div>

      <div className={styles.accordionContainer}>
        {sections.map((section) => (
          <div key={section} className={styles.accordionItem}>
            <div
              className={styles.accordionHeader}
              onClick={() => toggleSection(section)}
            >
              <div className={styles.sectionTitle}>
                <span>{getSectionTitle(section)}</span>
              </div>
              <span className={styles.toggleIcon}>
                {openSections[section] ? "−" : "+"}
              </span>
            </div>

            {openSections[section] && (
              <div className={styles.accordionContent}>
                {renderSectionContent(section)}

                {/* {error[section] && (
                  <div className={styles.errorMessage}>{error[section]}</div>
                )} */}
                {success[section] && (
                  <div className={styles.successMessage}>
                    {success[section]}
                  </div>
                )}
              </div>
            )}
            <hr className={styles.divider} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanConfig;
